/**
 * tizz-cart.js — adapter that patches Dawn's cart classes at runtime.
 *
 * ⚠️  THIS FILE REACHES INTO assets/cart.js AND assets/cart-drawer.js AND
 *     REWRITES THEIR PROTOTYPE METHODS. That is deliberate action-at-a-distance.
 *
 * Why not just fork those files? They are ~430 lines of working cart mechanics
 * (quantity updates, line removal, section re-rendering, live regions, error
 * handling). Forking means owning all of it forever and losing the ability to
 * diff against upstream Dawn. Every patch below is small, and each one names the
 * exact Dawn line it works around so the next person can check whether an
 * upstream update has made it unnecessary.
 *
 * Load order matters: this must run AFTER cart-drawer.js, which must run after
 * cart.js (CartDrawerItems extends CartItems at parse time). All three are
 * `defer`, so document order in layout/theme.liquid guarantees it. Every patch
 * is existence-guarded anyway, because on /cart the drawer is deliberately not
 * rendered.
 */
(function () {
  if (window.__tizzCartInit) return;
  window.__tizzCartInit = true;

  var CartDrawerEl = window.customElements && customElements.get('cart-drawer');
  var CartDrawerItemsEl = window.customElements && customElements.get('cart-drawer-items');
  var CartItemsEl = window.customElements && customElements.get('cart-items');

  /* ------------------------------------------------------------------ F1
     cart-drawer.js:96 returns [{id:'cart-drawer', selector:'#CartDrawer'},
     {id:'cart-icon-bubble'}].

     Two problems. (1) `cart-icon-bubble` is Dawn's header cart link, which only
     exists in sections/header.liquid — a section this theme never renders, since
     header-group.json contains only tizz_header. cart.js:187 dereferences
     document.getElementById(section.id) UNGUARDED, so its presence in this list
     throws. (2) The objects have no `section` key, but the five Tizz
     add-to-cart handlers read `.map(s => s.section)` — giving the literal string
     "undefined,undefined" as the sections param, which makes Shopify return
     {"undefined":null} and renderContents throw on null.innerHTML AFTER the item
     was already added.

     The handlers are being fixed to read `.id` (Dawn's own convention, see
     product-form.js:38), but `section` is included here too so this stays
     correct whichever key a caller reads.
  ------------------------------------------------------------------------- */
  if (CartDrawerEl) {
    CartDrawerEl.prototype.getSectionsToRender = function () {
      return [{ id: 'cart-drawer', section: 'cart-drawer', selector: '#CartDrawer' }];
    };
  }

  /* ------------------------------------------------------------------ F2
     Same cart-icon-bubble removal for cart-drawer.js:120.
  ------------------------------------------------------------------------- */
  if (CartDrawerItemsEl) {
    CartDrawerItemsEl.prototype.getSectionsToRender = function () {
      return [{ id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' }];
    };
  }

  /* ------------------------------------------------------------------ F3
     cart.js:122 — same cart-icon-bubble problem on the /cart page. This is a
     LIVE bug today, not a hypothetical: changing a quantity on /cart replaces
     the first section, then throws at cart.js:187, so the subtotal goes stale,
     publish(cartUpdate) at :218 never runs (header count never updates), and
     the shopper is told "There was an error while updating your cart."

     Also scopes the .js-contents selectors. One tizz-cart section now renders
     BOTH #main-cart-items and #main-cart-footer, and getSectionInnerHTML
     (cart.js:248) does querySelector on the whole parsed section — which would
     return the FIRST .js-contents for both, feeding the items markup into the
     footer. The scoped selectors work in both places the selector is used:
     el.querySelector() still matches descendants, and the parse-time lookup
     resolves against the full section document.
  ------------------------------------------------------------------------- */
  if (CartItemsEl) {
    CartItemsEl.prototype.getSectionsToRender = function () {
      var items = document.getElementById('main-cart-items');
      var footer = document.getElementById('main-cart-footer');
      var list = [];
      if (items) {
        list.push({ id: 'main-cart-items', section: items.dataset.id, selector: '#main-cart-items .js-contents' });
      }
      if (document.getElementById('cart-live-region-text')) {
        list.push({ id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' });
      }
      if (footer) {
        list.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '#main-cart-footer .js-contents' });
      }
      return list;
    };
  }

  /* ------------------------------------------------------------------ F4
     cart-drawer.js:74 clears `is-empty` from `.drawer__inner` — but the class is
     rendered on <cart-drawer> and <cart-drawer-items> (cart-drawer.liquid:20,
     :77), never on .drawer__inner. So it never clears, and after the FIRST add
     to an empty cart open() still sees is-empty, looks for .drawer__inner-empty
     (which the server just replaced with the non-empty markup), gets null, and
     trapFocus(null) throws in getFocusableElements.

     Highest-traffic path there is — first add of a session.
  ------------------------------------------------------------------------- */
  if (CartDrawerEl && CartDrawerEl.prototype.renderContents) {
    var originalRenderContents = CartDrawerEl.prototype.renderContents;
    CartDrawerEl.prototype.renderContents = function (parsedState) {
      this.classList.remove('is-empty');
      return originalRenderContents.call(this, parsedState);
    };
  }

  /* ------------------------------------------------------------------ F5
     cart.js:46 looks up `#Quantity-{id}` only. In the drawer the input is
     `#Drawer-quantity-{id}`, so any path through setValidity → resetQuantityInput
     throws there. Reached by typing an out-of-range quantity. (The stepper's own
     "− at 1" case is prevented in markup by min="1", but this is the net.)
  ------------------------------------------------------------------------- */
  if (CartItemsEl) {
    CartItemsEl.prototype.resetQuantityInput = function (id) {
      var input = this.querySelector('#Quantity-' + id) || this.querySelector('#Drawer-quantity-' + id);
      if (!input) return;
      input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    };
  }

  /* ------------------------------------------------------------------ F6
     Header cart count sync.

     Replaces tizz-header's old subscriber, which fetched /?sections=tizz-header
     — a ~10KB full section render per cart event, and one that renders the
     header standalone with SCHEMA DEFAULTS (orange background instead of the
     white set in header-group.json). It only copied textContent so it was safe,
     but it was one copy-paste away from painting default colours into the live
     header.

     Also closes a gap the drawer opens: once <cart-drawer> exists the add-to-cart
     handlers take the renderContents branch, which does NOT publish cartUpdate —
     so nothing would update the count on add. The handlers call this directly.
  ------------------------------------------------------------------------- */
  function applyCount(count) {
    document.querySelectorAll('[data-tizz-cart-count]').forEach(function (el) {
      el.textContent = count;
    });
    document.querySelectorAll('[data-tizz-cart-button]').forEach(function (el) {
      el.dataset.cartCount = count;
      el.setAttribute('aria-label', 'Cart, ' + count + ' item' + (count === 1 ? '' : 's'));
    });
  }

  window.tizzCartSync = function (knownCount) {
    if (typeof knownCount === 'number') {
      applyCount(knownCount);
      return Promise.resolve(knownCount);
    }
    // routes.cart_url, not a hardcoded path: on markets with a locale prefix
    // (/en-ca/) the AJAX cart endpoints are prefixed too. Matches cart.js:92/109.
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(cartUrl + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { applyCount(cart.item_count); return cart.item_count; })
      .catch(function () { /* leave the count as-is rather than showing a wrong number */ });
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      // cart.js:218 publishes with cartData; use it and skip the round-trip.
      var count = event && event.cartData && typeof event.cartData.item_count === 'number'
        ? event.cartData.item_count
        : undefined;
      window.tizzCartSync(count);
    });
  }

  /* ------------------------------------------------------------------ F7
     Free-shipping bar: fill animation + screen-reader status
     (snippets/tizz-free-shipping.liquid).

     Every cart change swaps in freshly rendered bar markup — renderContents,
     updateQuantity (F2/F3) and Dawn's onCartUpdate replaceWith alike. This
     watches the roots those renders write under — #CartDrawer, and on /cart
     #MainContent (which also survives a theme-editor reload of the cart
     section; <cart-items> does not) — and when a new bar arrives:

     - Animates the fill old → new with the Web Animations API. WAAPI leaves no
       inline style behind, so the server-rendered width is always the end
       state: nothing sticks mid-way in a hidden tab or if this throws. On
       add-to-cart the markup lands while the drawer is still closed (open()
       runs in a later setTimeout, then slides for 300ms), so the animation
       waits; fill: 'backwards' holds the old width meanwhile.
     - Announces the new message (WCAG 4.1.3 status messages). A live region
       only speaks when its content changes AFTER it is in the accessibility
       tree, and every render destroys whatever was there — so an empty
       role=status <span> goes inside the new bar (inside the aria-modal
       dialog, where VoiceOver listens) and gets its text a beat later. A
       <span>, because base.css hides p:empty and div:empty.
  ------------------------------------------------------------------------- */
  (function () {
    var roots = [
      document.getElementById('CartDrawer'),
      document.querySelector('cart-items') ? document.getElementById('MainContent') : null
    ].filter(Boolean);
    if (!roots.length || typeof MutationObserver !== 'function') return;

    // Scoped to the observed roots, so a bar rendered anywhere else on the page
    // can never be mistaken for the cart's.
    function findBar() {
      for (var i = 0; i < roots.length; i++) {
        var bar = roots[i].querySelector('[data-tizz-ship]');
        if (bar) return bar;
      }
      return null;
    }

    function messageOf(bar) {
      var msg = bar.querySelector('.tizz-ship__msg');
      return msg ? msg.textContent.replace(/\s+/g, ' ').trim() : '';
    }

    function announce(bar, text) {
      var live = document.createElement('span');
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      bar.appendChild(live);
      setTimeout(function () { live.textContent = text; }, 250);
    }

    var reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var lastEl = findBar();
    var lastPct = lastEl ? Number(lastEl.dataset.pct) : 0;
    var lastMsg = lastEl ? messageOf(lastEl) : '';

    // Our own writes (the status span, its text) re-enter here but find the
    // same bar and return at once; WAAPI makes no DOM mutations at all.
    var observer = new MutationObserver(function () {
      var el = findBar();
      if (el === lastEl) return;
      lastEl = el;
      if (!el) {
        lastPct = 0; // cart emptied: the next bar grows from nothing
        lastMsg = '';
        return;
      }

      var msg = messageOf(el);
      if (msg && msg !== lastMsg) announce(el, msg);
      lastMsg = msg;

      var from = lastPct;
      var pct = Number(el.dataset.pct);
      if (isNaN(pct)) return;
      lastPct = pct;

      var fill = el.querySelector('.tizz-ship__fill');
      if (from === pct || !fill || typeof fill.animate !== 'function') return;
      if ((reduced && reduced.matches) || el.hasAttribute('data-reduce-motion')) return;
      var drawer = el.closest('cart-drawer');
      fill.animate([{ width: from + '%' }, { width: pct + '%' }], {
        duration: 450,
        delay: drawer && !drawer.classList.contains('active') ? 300 : 0,
        fill: 'backwards',
        easing: 'cubic-bezier(0.32, 0.72, 0, 1)'
      });
    });
    roots.forEach(function (root) {
      observer.observe(root, { childList: true, subtree: true });
    });
  })();

  /* ------------------------------------------------------------------
     Escape closes the drawer from anywhere.

     cart-drawer.js:5 binds keyup on the <cart-drawer> element itself, so Escape
     only works once focus is already inside — which depends on the transitionend
     in open() having fired. Under prefers-reduced-motion, or if a transition is
     ever removed, that never happens and Escape silently stops working.
  ------------------------------------------------------------------------- */
  document.addEventListener('keydown', function (ev) {
    if (ev.key !== 'Escape') return;
    var drawer = document.querySelector('cart-drawer.active');
    if (drawer && typeof drawer.close === 'function') drawer.close();
  });
})();
